/**
 * Lightweight, dependency-free image quality heuristics — deliberately not a
 * full CV library. The pure pixel-analysis function (`analyzePixels`) is
 * unit-tested against synthetic pixel data; the browser-facing wrapper
 * (`checkImageQuality`) does real image decoding via Canvas/ImageBitmap,
 * which isn't meaningfully testable outside a real browser (jsdom doesn't
 * implement canvas pixel decoding) — verified manually against real bill
 * photos in the running app instead.
 */
export type QualityIssue = 'too_dark' | 'blank' | 'blurry' | 'low_resolution';

export interface QualityCheckResult {
  ok: boolean;
  issues: QualityIssue[];
}

/** Tunable, approximate thresholds — not exact science, calibrated against real bill photos as the app is used. */
export const QUALITY_THRESHOLDS = {
  minDimensionPx: 400,
  /** Mean grayscale luminance (0-255); below this, the photo is likely too dark to read. */
  minMeanLuminance: 30,
  /** Variance of grayscale luminance; below this, the frame is likely blank/mostly uniform (e.g. a wall, or out of frame). */
  minLuminanceVariance: 15,
  /** Variance of the discrete Laplacian (edge response); below this, the image likely lacks sharp text edges (blurry). */
  minLaplacianVariance: 18,
};

function toGrayscale(rgba: Uint8ClampedArray, width: number, height: number): Float32Array {
  const gray = new Float32Array(width * height);
  for (let i = 0; i < width * height; i++) {
    const r = rgba[i * 4] ?? 0;
    const g = rgba[i * 4 + 1] ?? 0;
    const b = rgba[i * 4 + 2] ?? 0;
    gray[i] = 0.299 * r + 0.587 * g + 0.114 * b;
  }
  return gray;
}

function meanAndVariance(values: ArrayLike<number>): { mean: number; variance: number } {
  const n = values.length;
  let sum = 0;
  for (let i = 0; i < n; i++) sum += values[i]!;
  const mean = sum / n;
  let sqSum = 0;
  for (let i = 0; i < n; i++) sqSum += (values[i]! - mean) ** 2;
  return { mean, variance: sqSum / n };
}

/** Variance of the discrete Laplacian (a standard, simple blur-detection heuristic: sharp edges produce high-variance responses, blur produces low). */
function laplacianVariance(gray: Float32Array, width: number, height: number): number {
  const responses: number[] = [];
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = y * width + x;
      const lap = gray[idx - width]! + gray[idx + width]! + gray[idx - 1]! + gray[idx + 1]! - 4 * gray[idx]!;
      responses.push(lap);
    }
  }
  if (responses.length === 0) return 0;
  return meanAndVariance(responses).variance;
}

/** Analyzes raw RGBA pixel data (e.g. from a downscaled canvas ImageData). Pure — no I/O. */
export function analyzePixels(rgba: Uint8ClampedArray, width: number, height: number): QualityCheckResult {
  const issues: QualityIssue[] = [];
  const gray = toGrayscale(rgba, width, height);
  const { mean, variance } = meanAndVariance(gray);

  if (mean < QUALITY_THRESHOLDS.minMeanLuminance) issues.push('too_dark');
  if (variance < QUALITY_THRESHOLDS.minLuminanceVariance) issues.push('blank');
  if (laplacianVariance(gray, width, height) < QUALITY_THRESHOLDS.minLaplacianVariance) issues.push('blurry');

  return { ok: issues.length === 0, issues };
}

const ANALYSIS_MAX_DIMENSION = 600;

/**
 * Decodes an image file/blob and runs the quality heuristics against a
 * downscaled copy (for speed — the ORIGINAL file is never modified or
 * replaced, this is purely a read-only check before upload).
 */
export async function checkImageQuality(source: Blob): Promise<QualityCheckResult> {
  const bitmap = await createImageBitmap(source);
  try {
    const issues: QualityIssue[] = [];
    if (Math.min(bitmap.width, bitmap.height) < QUALITY_THRESHOLDS.minDimensionPx) {
      issues.push('low_resolution');
    }

    const scale = Math.min(1, ANALYSIS_MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = new OffscreenCanvas(width, height);
    const ctx = canvas.getContext('2d');
    if (!ctx) return { ok: issues.length === 0, issues };

    ctx.drawImage(bitmap, 0, 0, width, height);
    const { data } = ctx.getImageData(0, 0, width, height);
    const pixelResult = analyzePixels(data, width, height);

    return { ok: issues.length === 0 && pixelResult.ok, issues: [...issues, ...pixelResult.issues] };
  } finally {
    bitmap.close();
  }
}
